import React from "react";
import {Button, Icon, TableData, useNotifications} from "peggirkit";
import {InformationCircleIcon} from "@heroicons/react/16/solid";
import {FormattedMessage, useIntl} from "react-intl";
import {BarsArrowUpIcon} from "@heroicons/react/24/outline";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {UpdatedOrdering} from "@/utils/Ordering";

type WithId = {
    id: number,
};

type Props<T extends WithId> = {
    isUpdatingOverview: boolean,
    tableData: TableData<T>[],
    persistFn: (restaurantId: number, updatedOrdering: UpdatedOrdering[]) => Promise<T[]>,
};

const UpdateOrdering = <T extends WithId, >({tableData, isUpdatingOverview, persistFn}: Props<T>) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant()
    const {mutate, isPending} = useMutation({
        mutationFn: (tableData: TableData<T>[]) =>
            persistFn(
                dashboardLocation.id,
                tableData.map((item: TableData<T>, index: number) => ({
                    id: item.id,
                    orderNumber: index,
                })),
            ),
        onSuccess: () => notifySuccess(intl.formatMessage({id: "saved"}), notify),
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
    });
    const disabledState = isUpdatingOverview || isPending;

    return (
        <div className={"w-full flex flex-col-reverse sm:flex-row items-end sm:items-center justify-end gap-4"}>
            <small
                className={"text-xs text-gray-500 dark:text-slate-400 flex flex-row items-center justify-start gap-1"}
            >
                <Icon
                    icon={InformationCircleIcon}
                    className={"size-4"}
                />
                <FormattedMessage id={"dragRowsToChangeOrder"}/>
            </small>

            <Button
                buttonAction={{
                    as: "button",
                    actionProps: {
                        onClick: e => {
                            e.preventDefault();
                            mutate(tableData);
                        },
                    },
                }}
                iconBefore={<Icon icon={BarsArrowUpIcon}/>}
                disabled={disabledState}
                loading={isPending}
            >
                <FormattedMessage id={"saveOrder"}/>
            </Button>
        </div>
    );
};

export default UpdateOrdering;